import React, { useMemo, useState, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
)

const MINT = '#95D0CC'
const CHARCOAL = '#303B45'
const MINT_FILL = 'rgba(149, 208, 204, 0.22)'

function formatDowntime(minutes) {
  if (minutes == null || minutes <= 0) return '0 min'
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  return m ? `${h}h ${m}min` : `${h}h`
}

function segmentClass(overall, up) {
  // up=false → downtime real del portal. overall error/warn con up=true → degradado (sigue sirviendo).
  if (!up) return 'Unavailable'
  if (overall === 'error' || overall === 'warn') return 'NotReady'
  if (overall === 'unknown') return 'Unknown'
  return 'Ready'
}

function formatPointLabel(at, fallback) {
  try {
    return new Date(at).toLocaleString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return fallback
  }
}

function formatWhen(at) {
  try {
    return new Date(at).toLocaleString('es-CL', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

/**
 * Uptime / downtime — estilo portal (mint #95D0CC, charcoal #303B45).
 * Click en baja o franja Unavailable → detalle de probes en error.
 */
export function PortalUptimeChart({ uptime }) {
  const [selected, setSelected] = useState(null)

  const series = useMemo(
    () =>
      (uptime?.points || []).filter(
        (p) =>
          p &&
          p.samples > 0 &&
          p.upPct != null &&
          Number.isFinite(Number(p.upPct)),
      ),
    [uptime?.points],
  )

  const chartData = useMemo(() => {
    if (series.length === 0) {
      return { labels: [], datasets: [], empty: true }
    }

    const labels = series.map((p) => formatPointLabel(p.at, p.label))
    const values = series.map((p) => Number(p.upPct))
    const few = series.length < 10

    return {
      empty: false,
      labels,
      datasets: [
        {
          label: 'Checks OK (superficie portal) %',
          data: values,
          borderColor: MINT,
          backgroundColor: MINT_FILL,
          borderWidth: 2.5,
          fill: 'origin',
          tension: series.length < 3 ? 0 : 0.3,
          spanGaps: false,
          pointBackgroundColor: (ctx) => {
            const p = series[ctx.dataIndex]
            if (p && Number(p.upPct) < 99.5) return '#D75E64'
            return MINT
          },
          pointBorderColor: '#fff',
          pointRadius: (ctx) => {
            const p = series[ctx.dataIndex]
            if (p && Number(p.upPct) < 99.5) return few ? 5 : 4
            return few ? 4 : 2
          },
          pointHoverRadius: 7,
          pointBorderWidth: 2,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: CHARCOAL,
          pointHoverBorderWidth: 2,
          segment: {
            borderColor: (ctx) => {
              const y0 = ctx.p0?.parsed?.y
              const y1 = ctx.p1?.parsed?.y
              if (y0 == null || y1 == null) return MINT
              if (y0 < 99.5 || y1 < 99.5) return '#D75E64'
              return MINT
            },
          },
        },
      ],
    }
  }, [series])

  const selectPoint = useCallback(
    (index) => {
      const p = series[index]
      if (!p) return
      setSelected({
        kind: 'interval',
        at: p.at,
        title: formatWhen(p.at),
        upPct: p.upPct,
        samples: p.samples,
        downSamples: p.downSamples,
        failures: p.failures || [],
        note:
          p.downSamples > 0 && !(p.failures || []).length
            ? 'Este intervalo es anterior al registro de detalle de fallas, o el sample no guardó probes.'
            : null,
      })
    },
    [series],
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 400, easing: 'easeOutQuart' },
      interaction: { mode: 'index', intersect: false, axis: 'x' },
      onClick: (_evt, elements) => {
        if (!elements?.length) return
        selectPoint(elements[0].index)
      },
      onHover: (evt, elements) => {
        const canvas = evt?.native?.target
        if (canvas?.style) {
          canvas.style.cursor = elements?.length ? 'pointer' : 'default'
        }
      },
      layout: { padding: { top: 8, right: 12, bottom: 0, left: 4 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: CHARCOAL,
          titleColor: '#fff',
          bodyColor: '#E8F6F5',
          borderColor: 'rgba(149,208,204,0.35)',
          borderWidth: 1,
          padding: 10,
          displayColors: false,
          cornerRadius: 8,
          titleFont: { size: 12, weight: '600' },
          bodyFont: { size: 12 },
          callbacks: {
            title: (items) => {
              const i = items[0]?.dataIndex
              const p = series[i]
              if (!p) return ''
              return formatWhen(p.at)
            },
            label: (item) => {
              const p = series[item.dataIndex]
              if (!p) return ''
              const pct = item.parsed.y
              const down =
                p.downSamples > 0 ? ` · ${p.downSamples} caídas` : ''
              return `${pct}% · ${p.samples} checks${down}`
            },
            afterBody: (items) => {
              const p = series[items[0]?.dataIndex]
              const fails = p?.failures || []
              if (!fails.length) {
                if (p?.downSamples > 0) {
                  return ['Click para detalle (puede no haber fallas guardadas)']
                }
                return []
              }
              const lines = fails.slice(0, 4).map((f) => {
                const sum = f.summary ? `: ${f.summary}` : ''
                return `• ${f.label}${sum}`
              })
              if (fails.length > 4) {
                lines.push(`… y ${fails.length - 4} más (click)`)
              } else {
                lines.push('Click para ver detalle completo')
              }
              return lines
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: true,
            color: 'rgba(48, 59, 69, 0.05)',
            drawTicks: false,
          },
          border: { color: 'rgba(48, 59, 69, 0.1)' },
          ticks: {
            color: '#6B7280',
            font: { size: 11 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10,
            padding: 6,
          },
        },
        y: {
          min: 0,
          max: 100,
          suggestedMin: 0,
          suggestedMax: 100,
          grid: {
            display: true,
            color: 'rgba(48, 59, 69, 0.06)',
          },
          border: { display: false },
          ticks: {
            color: '#6B7280',
            font: { size: 11 },
            padding: 6,
            stepSize: 25,
            callback: (v) => `${v}%`,
          },
        },
      },
    }),
    [series, selectPoint],
  )

  const stripSegments = useMemo(() => {
    const segs = uptime?.segments || []
    if (segs.length === 0) return []
    const out = []
    for (const s of segs) {
      const cls = segmentClass(s.overall, s.up)
      const last = out[out.length - 1]
      if (last && last.cls === cls) {
        last.count += 1
        last.end = s.at
        if (!s.up) {
          last.downSamples = (last.downSamples || 0) + 1
          mergeFailuresInto(last.failures, s.failures)
        }
      } else {
        out.push({
          cls,
          count: 1,
          start: s.at,
          end: s.at,
          overall: s.overall,
          downSamples: s.up ? 0 : 1,
          failures: s.up ? [] : [...(s.failures || [])],
          summary: s.summary,
        })
      }
    }
    return out
  }, [uptime?.segments])

  const selectStrip = useCallback((seg) => {
    if (seg.cls !== 'Unavailable' && seg.cls !== 'NotReady') {
      setSelected(null)
      return
    }
    setSelected({
      kind: 'strip',
      at: seg.start,
      title: `${formatWhen(seg.start)} → ${formatWhen(seg.end)}`,
      upPct: null,
      samples: seg.count,
      downSamples: seg.downSamples || seg.count,
      failures: seg.failures || [],
      note:
        seg.cls === 'Unavailable' && !(seg.failures || []).length
          ? 'Franja Unavailable sin detalle de probes (samples anteriores al registro de fallas).'
          : seg.cls === 'NotReady'
            ? 'NotReady = degradado (sigue sirviendo). Las fallas listadas son probes en error si las hay.'
            : null,
    })
  }, [])

  if (!uptime) return null

  return (
    <div className="ph-uptime">
      <div className="ph-uptime-side">
        <div className="ph-uptime-metrics">
          <div className="ph-uptime-metric">
            <span className="ph-uptime-metric-value">
              {uptime.samples === 0 ? '—' : `${uptime.uptimePct}%`}
            </span>
            <span className="ph-uptime-metric-label">
              Checks OK · {uptime.windowHours}h
            </span>
          </div>
          <div className="ph-uptime-metric">
            <span className="ph-uptime-metric-value ph-uptime-metric-value--muted">
              {formatDowntime(uptime.downtimeMinutes)}
            </span>
            <span className="ph-uptime-metric-label">Downtime estimado</span>
          </div>
          <div className="ph-uptime-metric">
            <span className="ph-uptime-metric-value ph-uptime-metric-value--muted">
              {uptime.samples}
            </span>
            <span className="ph-uptime-metric-label">Checks registrados</span>
          </div>
        </div>

        <div className="ph-uptime-strip-wrap" aria-label="Franja de estado">
          <div className="ph-uptime-strip">
            {stripSegments.length === 0 ? (
              <div className="ph-uptime-strip-empty" title="Sin historial aún" />
            ) : (
              stripSegments.map((seg, i) => (
                <button
                  type="button"
                  key={`${seg.start}-${i}`}
                  className={`ph-uptime-seg ph-uptime-seg--${seg.cls}${
                    seg.cls === 'Unavailable' || seg.cls === 'NotReady'
                      ? ' ph-uptime-seg--clickable'
                      : ''
                  }`}
                  style={{ flexGrow: seg.count }}
                  title={
                    seg.cls === 'Unavailable' || seg.cls === 'NotReady'
                      ? `${seg.cls} · ${seg.count} check(s) · click para detalle`
                      : `${seg.cls} · ${seg.count} check(s)`
                  }
                  onClick={() => selectStrip(seg)}
                />
              ))
            )}
          </div>
          <div className="ph-uptime-strip-legend">
            <span>
              <i className="ph-uptime-leg ph-uptime-leg--Ready" /> Ready
            </span>
            <span>
              <i className="ph-uptime-leg ph-uptime-leg--NotReady" /> NotReady
            </span>
            <span>
              <i className="ph-uptime-leg ph-uptime-leg--Unavailable" /> Unavailable
            </span>
            <span className="ph-uptime-strip-range">
              últimas {uptime.windowHours}h · click en baja = detalle
            </span>
          </div>
        </div>
      </div>

      <div className="ph-uptime-chart-panel">
        <div className="ph-uptime-chart-head">
          <h3>Checks OK (superficie portal) %</h3>
          <span>
            {series.length > 0
              ? `${series.length} intervalos · click en baja para ver causa`
              : 'Sin serie aún'}
          </span>
        </div>
        <div className="ph-uptime-chart-wrap">
          {chartData.empty ? (
            <div className="ph-uptime-chart-empty">
              <p>Recolectando historial…</p>
              <span>Los checks cada ~2 min irán dibujando la curva.</span>
            </div>
          ) : (
            <Line data={chartData} options={options} />
          )}
        </div>

        {selected ? (
          <div className="ph-uptime-detail">
            <div className="ph-uptime-detail-head">
              <div>
                <h4>Detalle de la baja</h4>
                <p className="ph-uptime-detail-meta">
                  {selected.title}
                  {selected.upPct != null
                    ? ` · ${selected.upPct}% OK · ${selected.samples} checks · ${selected.downSamples} caídas`
                    : ` · ${selected.samples} checks`}
                </p>
              </div>
              <button
                type="button"
                className="ph-btn ph-btn-link"
                onClick={() => setSelected(null)}
              >
                Cerrar
              </button>
            </div>
            {selected.failures?.length ? (
              <ul className="ph-uptime-detail-list">
                {selected.failures.map((f) => (
                  <li key={`${f.id}-${f.summary || ''}`}>
                    <strong>{f.label}</strong>
                    <code className="ph-uptime-detail-id">{f.id}</code>
                    {f.summary ? (
                      <span className="ph-uptime-detail-summary">
                        {f.summary}
                      </span>
                    ) : (
                      <span className="ph-muted">Sin summary en el sample</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ph-empty">
                {selected.note ||
                  'No hay fallas de probes registradas en este intervalo.'}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function mergeFailuresInto(into, add) {
  const list = into || []
  for (const f of add || []) {
    if (!f?.id) continue
    const key = `${f.id}::${f.summary || ''}`
    const idx = list.findIndex((x) => `${x.id}::${x.summary || ''}` === key)
    if (idx >= 0) list[idx] = f
    else list.push(f)
  }
  return list
}

export default PortalUptimeChart
